import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const CATS = [
  { v: "all", n: "All" },
  { v: "india_accounting", n: "Accounting (IN)" },
  { v: "india_tax", n: "Income Tax (IN)" },
  { v: "india_compliance", n: "Compliance (IN)" },
  { v: "india_incorporation", n: "Incorporation (IN)" },
  { v: "india_loan", n: "Loans (IN)" },
  { v: "usa_accounting", n: "Accounting (US)" },
  { v: "usa_incorporation", n: "Incorporation (US)" },
];

export default function Services() {
  const [items, setItems] = useState([]);
  const [region, setRegion] = useState("all");
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { (async () => {
    const r = await api.get("/services");
    setItems(r.data);
  })(); }, []);

  const filtered = useMemo(() => items.filter(s =>
    (region === "all" || s.region === region) &&
    (cat === "all" || s.category === cat) &&
    (!q || s.title.toLowerCase().includes(q.toLowerCase()))
  ), [items, region, cat, q]);

  const order = async (svc) => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "client") { toast.error("Only clients can place orders"); return; }
    try {
      const r = await api.post("/orders", { service_id: svc.service_id });
      toast.success("Order created. Proceed to payment.");
      nav(`/orders/${r.data.order_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="services-page">
      <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
        <div className="md:col-span-8">
          <div className="text-xs uppercase tracking-[0.2em] text-blue-600 font-bold mb-2">Catalog</div>
          <h1 className="font-display text-5xl font-black tracking-tight text-slate-900">Services & pricing</h1>
        </div>
        <div className="md:col-span-4 flex gap-2">
          <Input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} data-testid="services-search" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="w-40" data-testid="services-region"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All regions</SelectItem>
            <SelectItem value="IN">India</SelectItem>
            <SelectItem value="US">USA</SelectItem>
          </SelectContent>
        </Select>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-56" data-testid="services-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            {CATS.map(c => <SelectItem key={c.v} value={c.v}>{c.n}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500 ml-auto">{filtered.length} services</span>
      </div>

      <div className="grid md:grid-cols-3 gap-5" data-testid="services-grid">
        {filtered.map(s => (
          <div key={s.service_id} className="tracing-card border border-slate-200 rounded p-6 bg-white flex flex-col" data-testid={`svc-card-${s.service_id}`}>
            <div className="flex items-center justify-between mb-3">
              <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{s.region === "IN" ? "India" : "USA"}</Badge>
              <span className="text-xs text-slate-500">{s.delivery_days}d</span>
            </div>
            <h4 className="font-bold text-lg text-slate-900 leading-tight">{s.title}</h4>
            <p className="text-sm text-slate-600 mt-2 flex-1">{s.description}</p>
            <div className="mt-5 flex items-end justify-between">
              <div>
                <div className="font-display text-3xl font-black text-slate-900">
                  {s.currency === "INR" ? "₹" : "$"}{Number(s.price).toLocaleString()}
                </div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500">{s.currency} · all-inclusive</div>
              </div>
              <Button className="btn-primary" onClick={() => order(s)} data-testid={`order-btn-${s.service_id}`}>Order</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
